import { Button, IconButton } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";

import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import ItemStatus from "../../../../../../common/components/item_status";
import dayjs from "dayjs";

/**
 * GenerateObraArquivoColumns
 */

type Props = {
  onActionEditar: (idObraArquivo: number | null, openState: boolean) => void,
  onActionDeletar: (idObraArquivo: number | null, openState: boolean) => void,
  onActionView: (idObraArquivo: number, nomeArquivo: string) => void,
  onActionDownload: (idObraArquivo: number, nomeArquivo: string) => void,
};

export const GenerateObraArquivoColumns = ({
  onActionEditar,
  onActionDeletar,
  onActionView,
  onActionDownload,
}: Props): GridColDef[] => {
  const computeItemStatusTitle = (status: number) => {
    switch (status) {
      case 1:
        return "DE";
      case 2:
        return "DV";
      default:
        return "?";
    }
  };

  const computeItemStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return "yellow";
      case 2:
        return "red";
      default:
        return "blue";
    }
  };

  const computeItemStatusDescription = (status: number) => {
    switch (status) {
      case 1:
        return "Em dia";
      case 2:
        return "Vencido";
      default:
        return "?";
    }
  };

  const computeItemSituacaoTitle = (status: number) => {
    switch (status) {
      case 1:
        return "Atual";
      case 2:
        return "Obsoleto";
      default:
        return "?";
    }
  };

  const computeItemSituacaoColor = (status: number) => {
    switch (status) {
      case 1:
        return "#45b6af";
      case 2:
        return "#f3565d";
      default:
        return "blue";
    }
  };

  return [
    {
      field: "obsoleto",
      filterable: false,
      headerName: "Status",
      flex: 0,
      minWidth: 80,
      sortable: false,
      headerClassName: 'funcionarios-header',
      renderCell: (params) => {
        return (
          <div className="w-full h-full flex justify-center items-center">
            <div>
              <span
                className='inline px-2 py-1 text-sm rounded'
                style={{ backgroundColor: computeItemSituacaoColor(params.value ? 2 : 1) }}>
                {computeItemSituacaoTitle(params.value ? 2 : 1)}
              </span>
            </div>
          </div>
        );
      }
    },
    {
      field: "situacao",
      filterable: false,
      headerName: "Situação",
      minWidth: 75,
      width: 75,
      sortable: false,
      headerClassName: 'funcionarios-header',
      valueGetter: (_, rows) => rows['dataVencimento'],
      renderCell: (params) => {
        const vencido = params.value
          ? dayjs().isAfter(dayjs(params.value), 'day')
          : null;

        return (
          <div className="w-full h-full flex justify-center items-center">
            {params.value &&
              <ItemStatus
                title={computeItemStatusTitle(vencido ? 2 : 1)}
                color={computeItemStatusColor(vencido ? 2 : 1)}
                description={computeItemStatusDescription(vencido ? 2 : 1)} />}
          </div>
        );
      }
    },
    {
      field: "descricao",
      filterable: false,
      headerName: "Descrição",
      flex: 1,
      minWidth: 150,
      sortable: false,
      headerClassName: 'funcionarios-header',
    },
    {
      field: "nome",
      filterable: false,
      headerName: "Visualizar",
      width: 175,
      sortable: false,
      resizable: false,
      headerClassName: 'funcionarios-header',
      valueGetter: (_, rows) => ({
        idObraArquivo: rows['idObraArquivo'],
        arquivoNome: rows['nome'],
      }),
      renderCell: (params) => {
        const maxSize = 18;

        const idObraArquivo = params.value['idObraArquivo'] as number;
        const arquivoNome = params.value['arquivoNome'] as string;

        return (
          <Button
            onMouseDown={() => onActionView(idObraArquivo, arquivoNome)}
            sx={{
              fontSize: 11,
              paddingX: '.4rem',
              paddingY: 0,
              background: '#e6e6e6',
              borderLeft: '.3rem solid #45b6af',
              borderRadius: 0,
              color: 'black',
              fontWeight: 'normal',
              width: '9.5rem',
            }}>
            {arquivoNome && arquivoNome.length > maxSize ? `${arquivoNome.substring(0, maxSize)}...` : arquivoNome}&nbsp;
          </Button>
        );
      },
    },
    {
      field: "nomeArquivo",
      filterable: false,
      headerName: "Arquivo",
      width: 175,
      sortable: false,
      resizable: false,
      headerClassName: 'funcionarios-header',
      valueGetter: (_, rows) => ({
        idObraArquivo: rows['idObraArquivo'],
        arquivoNome: rows['nome'],
      }),
      renderCell: (params) => {
        const maxSize = 18;

        const idObraArquivo = params.value['idObraArquivo'] as number;
        const arquivoNome = params.value['arquivoNome'] as string;

        return (
          <Button
            onMouseDown={() => onActionDownload(idObraArquivo, arquivoNome)}
            sx={{
              fontSize: 11,
              paddingX: '.4rem',
              paddingY: 0,
              background: '#e6e6e6',
              borderLeft: '.3rem solid #45b6af',
              borderRadius: 0,
              color: 'black',
              fontWeight: 'normal',
              width: '9.5rem',
            }}>
            {arquivoNome && arquivoNome.length > maxSize ? `${arquivoNome.substring(0, maxSize)}...` : arquivoNome}&nbsp;
          </Button>
        );
      },
    },
    {
      field: "dataVencimento",
      filterable: false,
      headerName: "Data Vencimento",
      minWidth: 150,
      flex: 0,
      sortable: false,
      headerClassName: 'funcionarios-header',
      renderCell: (params) => {
        return (
          params.value &&
          <div>{dayjs.utc(params.value).local().format('DD/MM/YYYY')}</div>
        );
      },
    },
    {
      field: 'idObraArquivo',
      sortable: false,
      filterable: false,
      headerName: 'Ações',
      width: 100,
      minWidth: 100,
      renderCell: (params) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton onClick={() => onActionEditar(params.value, true)}>
              <DynamicIcon nameIcon="EditRounded" size={25} className="dark:text-white text-primary-green" />
            </IconButton>

            <IconButton onClick={() => onActionDeletar(params.value, true)}>
              <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
            </IconButton>
          </div>
        );
      }
    }
  ];
}